import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AppSnackbar from "layouts/notifications/Snackbar"; // font-awesome
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { getStates, getDistricts } from "services/sdbService";
import { updateUser } from "services/userService";
import { UI_LOCALE_OPTIONS, normalizeUserUiLanguage } from "constants/uiLocales";
import { useTranslation } from "hooks/useTranslation";

//  Helper validation function (can be moved to utils/validators.js)
function validateForm(form) {
  const errors = {};

  if (!form.first_name) errors.first_name = "First Name is required";
  if (!form.last_name) errors.last_name = "Last Name is required";

  // Phone Number Validation
  if (!form.phoneNumber) {
    errors.phoneNumber = "Phone number is required";
  } else if (!/^\d{10}$/.test(form.phoneNumber)) {
    errors.phoneNumber = "Phone must be a 10-digit number";
  }

  //  email_id Validation
  if (!form.email_id) {
    errors.email_id = "Email id is required";
    // } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email_id)) {
  } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email_id)) {
    errors.email_id = "Email is invalid";
  }

  if (!form.state) errors.state = "State is required";
  if (!form.district) errors.district = "District is required";
  if (!form.language) errors.language = "Language is required";

  return errors;
}

export default function EditUser({ open, onClose, user, onSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phoneNumber: "",
    email_id: "",
    state: "",
    district: "",
    language: "",
  });
  const [openDropDown, setOpenDropdown] = useState({});
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  // Toast-snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "",
    message: "",
  });
  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };
  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
      type: "",
      message: "",
    }));
  };

  //  Reset form on modal open
  useEffect(() => {
    const fetchData = async () => {
      try {
        const states = await getStates(user.country);
        setStates(states);
        setSelectedState(user.state);
        const districts = await getDistricts(user.state);
        setDistricts(districts);
      } catch (error) {
        console.error("Failed to fetch dropdown & user data", error);
      }
    };

    if (open && user) {
      setForm({
        first_name: user.first_name,
        last_name: user.last_name,
        phoneNumber: user.phone_number,
        email_id: user.email_id,
        state: user.state,
        district: user.district,
        language: user.language,
      });
      fetchData();
      setErrors({});
    }
  }, [open, user]);

  /* set district & blocks dropdwon */

  /* handlers for form fields on change */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleState = (field) => async (e) => {
    const value = e.target.value;
    setForm({ ...form, [field]: value });
    setSelectedState(value);
    const districts = await getDistricts(value);
    setDistricts(districts);
  };

  const handleDistrict = (field) => async (e) => {
    const value = e.target.value;
    setForm({ ...form, [field]: value });
    setSelectedDistrict(value);
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(form);
    console.log("form", form);
    console.log("validationErrors", validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      console.log("Submitting form:", form);
      await updateUserDetails(form);
      onClose();
      onSuccess?.();
    } else {
      setErrors(validationErrors);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: "",
      last_name: "",
      phoneNumber: "",
      email_id: "",
      state: "",
      district: "",
      language: "",
    });
    setErrors({});
    onClose();
  };

  const handleNumericChange = (field) => (e) => {
    const value = e.target.value;
    // Allow only digits (or empty string)
    if (/^\d*$/.test(value)) {
      setForm({ ...form, [field]: value });
    }
  };

  /* update annotator api call */
  async function updateUserDetails(form) {
    let userObj = {
      user_id: user.id,
      first_name: form.first_name,
      last_name: form.last_name,
      phone_number: form.phoneNumber,
      email_id: form.email_id,
      state: form.state,
      district: form.district,
      language: form.language,
    };

    let result = await updateUser(userObj);
    if (result.status == "success") {
      showSnackbar("success", result.message);
      onClose();
    } else {
      showSnackbar("error", result.message);
    }
  }

  const renderSelectField = (
    label,
    field,
    field_value,
    options,
    handler,
    option_value,
    option_label
  ) => (
    <FormControl fullWidth error={Boolean(errors[field])}>
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        value={field_value}
        label={label}
        onChange={handler ? handler(field) : ""}
        sx={{
          height: 50,
          fontSize: "1rem",
          "& svg": {
            top: "calc(50% - 0.5em)",
            right: 10,
            position: "absolute",
            pointerEvents: "none",
            color: "gray",
          },
        }}
        onOpen={() => setOpenDropdown((prev) => ({ ...prev, [field]: true }))}
        onClose={() => setOpenDropdown((prev) => ({ ...prev, [field]: false }))}
        IconComponent={() => (
          <FontAwesomeIcon icon={openDropDown[field] ? faChevronUp : faChevronDown} />
        )}
      >
        {option_value != "" && option_label != ""
          ? options.map((option) => (
              <MenuItem key={option[option_value]} value={option[option_value]}>
                {option[option_label]}
              </MenuItem>
            ))
          : options.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
      </Select>
      {errors[field] && (
        <p style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>{errors[field]}</p>
      )}
    </FormControl>
  );

  const renderTextField = (label, field, field_value, type = "text") => (
    <TextField
      label={label}
      type={type}
      fullWidth
      value={field_value}
      onChange={handleChange(field)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      variant="outlined"
      sx={{ fontSize: "1rem" }}
    />
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">Edit annotator</MDTypography>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderTextField("First Name", "first_name", form.first_name)}
            </Grid>
            <Grid item xs={6}>
              {renderTextField("Last Name", "last_name", form.last_name)}
            </Grid>
            <Grid item xs={6}>
              {/* {renderTextField("Phone Number", "phoneNumber")} */}
              <TextField
                label="Phone Number"
                fullWidth
                type="text"
                value={form.phoneNumber}
                onChange={handleNumericChange("phoneNumber")}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
                variant="outlined"
                inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ fontSize: "0.875rem", color: "#666" }}>+57</span>
                    </InputAdornment>
                  ),
                }}
                sx={{ fontSize: "1rem" }}
              />
            </Grid>
            <Grid item xs={6}>
              {renderTextField("Email id", "email_id", form.email_id)}
            </Grid>
            <Grid item xs={6}>
              {renderSelectField(
                "State",
                "state",
                form.state,
                states,
                handleState,
                "state",
                "state"
              )}
            </Grid>
            <Grid item xs={6}>
              {renderSelectField(
                "District",
                "district",
                form.district,
                districts,
                handleDistrict,
                "district",
                "district"
              )}
            </Grid>
            <Grid item xs={6}>
              {renderSelectField(
                t("superAdmin.modals.portalLanguage", "Portal language"),
                "language",
                form.language,
                UI_LOCALE_OPTIONS,
                handleChange,
                "code",
                "label"
              )}
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  backgroundColor: "#28a745", // Bootstrap success green
                  color: "#fff",
                  border: "2px solid #218838",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none", // Prevent ALL CAPS
                  fontSize: "1rem",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#218838", // Darker green on hover
                    borderColor: "#1e7e34",
                  },
                  "&:active": {
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                  },
                }}
              >
                Save
              </Button>
              <Button onClick={handleCancel} variant="contained" color="success">
                Cancel
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

EditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};
